import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { User, Menu, Globe, HelpCircle, ExternalLink, Home, CircleDollarSign, Link, Rocket, Target, Calculator, Wallet, DollarSign, Boxes, Settings, Calendar, StopCircle, ChevronLeft, Shield, Copy } from 'lucide-react';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useLocation } from 'react-router-dom';
import { WrenchIcon } from '../components/ui/icons/WrenchIcon';
import { ChevronDown, Check } from 'lucide-react';
import { QRCode } from '../components/ui/QRCode';
import { Eye } from 'lucide-react';
import { EyeClosed } from '../components/ui/icons/EyeClosed';
import { isUserAdmin } from '../utils/adminUtils';
import { useBots } from '../hooks/useBots';
import { useLanguage } from '../hooks/useLanguage';
import { useWebSocket } from '../contexts/WebSocketContext';

import { ChatMessage } from '../components/ChatMessage';
import { ChatMessageGroup } from '../components/ChatMessageGroup';
import { groupMessagesByDate } from '../utils/chatUtils';
import { formatMessage, formatDate } from '../utils/messageTemplates';
import { getCryptoIcons } from '../utils/botIcons';
import { getBotMapping } from '../utils/botMapping';
import { StatsModal } from '../components/StatsModal';
import { OrdersModal } from '../components/OrdersModal';
import { ParametersModal } from '../components/ParametersModal';
import { OrderModal } from '../components/OrderModal';

// SVG-иконка Telegram
const TelegramIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#229ED9"/>
    <path d="M17.5 7.5L15.5 17.5C15.5 17.5 15.25 18 14.75 18C14.5 18 14.25 17.75 14 17.5L11.5 15.5L10.5 16.5C10.5 16.5 10.5 16.5 10.25 16.5C10 16.5 10 16.25 10 16.25L9.5 13.5L15.25 8.75C15.5 8.5 15.25 8.5 15 8.75L8.5 13L6.5 12.5C6.5 12.5 6 12.25 6 12C6 11.75 6.5 11.5 6.5 11.5L17 7.25C17.25 7.25 17.5 7.25 17.5 7.5Z" fill="#fff"/>
  </svg>
);

export const UserPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage, switchLanguage } = useLanguage();
  const [user, setUser] = React.useState<{ user_id: string; email: string; password: string; twofa_enabled?: number; is_admin?: boolean; disclaimer_accepted?: boolean; disclaimer_accepted_at?: string | null } | null>(null);
  const [showDisclaimerModal, setShowDisclaimerModal] = React.useState<boolean>(false);
  const [openAccordion, setOpenAccordion] = React.useState<string | null>(null);

  // Функция для открытия аккордеона с автоматическим скрытием API ключей
  const handleAccordionToggle = (accordionName: string) => {
    if (openAccordion === accordionName) {
      setOpenAccordion(null);
    } else {
      setOpenAccordion(accordionName);
      // Скрываем API ключи при открытии другого аккордеона
      if (accordionName !== 'api_keys') {
        setShowApiKeys(false);
        setApiKeysData(null);
      }
    }
  };
  // Получаем twofa_enabled из user
  const is2FAEnabled = user?.twofa_enabled === 1;

  // --- 2FA state ---
  const [twofaStep, setTwofaStep] = React.useState<'init' | 'email_sent' | 'email_verified' | 'final'>(
    'init'
  );
  const [emailCode, setEmailCode] = React.useState('');
  const [emailCodeError, setEmailCodeError] = React.useState('');
  const [qrData, setQrData] = React.useState<{ secret: string; otpauth_url: string; qr_b64: string } | null>(null);
  const [totpCode, setTotpCode] = React.useState('');
  const [totpError, setTotpError] = React.useState('');
  const [twofaLoading, setTwofaLoading] = React.useState(false);
  const [twofaSuccess, setTwofaSuccess] = React.useState(false);
  const userEmail = user?.email || '';

  // --- Password Change state ---
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [repeatNewPassword, setRepeatNewPassword] = React.useState('');
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showRepeatNewPassword, setShowRepeatNewPassword] = React.useState(false);
  const [pwEmailCode, setPwEmailCode] = React.useState('');
  const [pw2faCode, setPw2faCode] = React.useState('');
  const [pwStep, setPwStep] = React.useState<'init'|'code_sent'|'success'>('init');
  const [pwLoading, setPwLoading] = React.useState(false);
  const [pwError, setPwError] = React.useState('');
  const [pwSuccess, setPwSuccess] = React.useState('');
  const [remindPasswordLoading, setRemindPasswordLoading] = React.useState(false);
  const [remindPasswordMessage, setRemindPasswordMessage] = React.useState<string | null>(null);

  // --- API Keys state ---
  const [accessKey, setAccessKey] = React.useState('');
  const [secretKey, setSecretKey] = React.useState('');
  const [apiKeysLoading, setApiKeysLoading] = React.useState(false);
  const [apiKeysError, setApiKeysError] = React.useState('');
  const [apiKeysSuccess, setApiKeysSuccess] = React.useState('');
  const [hasApiKeys, setHasApiKeys] = React.useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = React.useState(false);
  const [showApiKeys, setShowApiKeys] = React.useState(false);
  const [apiKeysData, setApiKeysData] = React.useState<{ access_key: string; secret_key: string } | null>(null);

  // --- Unread Messages state ---
  const [unreadMessages, setUnreadMessages] = React.useState<{ [botId: string]: number }>({
    'kas': 0,
    'sol': 0,
    'bnb_usdt': 0,
    'btc_usdc': 0,
    'btc_usdt': 0,
    'doge_usdt': 0,
    'eth_usdt': 0,
    'xrp_usdt': 0,
  });

  // --- WebSocket Context ---
  const { lastUnreadCounts, lastNewMessages } = useWebSocket();



  // Функция для копирования адреса кошелька
  const copyWalletAddress = async () => {
    const walletAddress = process.env.REACT_APP_WALLET_ADDRESS || '';
    
    try {
      // Пробуем современный API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(walletAddress);
        console.log('Адрес скопирован через Clipboard API');
      } else {
        // Fallback для мобильных устройств - создаем видимое поле для копирования
        const textArea = document.createElement('textarea');
        textArea.value = walletAddress;
        textArea.style.position = 'fixed';
        textArea.style.top = '50%';
        textArea.style.left = '50%';
        textArea.style.transform = 'translate(-50%, -50%)';
        textArea.style.width = '300px';
        textArea.style.height = '100px';
        textArea.style.zIndex = '9999';
        textArea.style.backgroundColor = 'white';
        textArea.style.border = '2px solid #000';
        textArea.style.borderRadius = '8px';
        textArea.style.padding = '10px';
        textArea.style.fontSize = '14px';
        textArea.style.fontFamily = 'monospace';
        textArea.readOnly = true;
        
        // Добавляем инструкцию
        const instruction = document.createElement('div');
        instruction.style.position = 'fixed';
        instruction.style.top = 'calc(50% - 80px)';
        instruction.style.left = '50%';
        instruction.style.transform = 'translate(-50%, -50%)';
        instruction.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
        instruction.style.color = 'white';
        instruction.style.padding = '10px';
        instruction.style.borderRadius = '8px';
        instruction.style.fontSize = '14px';
        instruction.style.zIndex = '10000';
        instruction.style.textAlign = 'center';
        instruction.style.maxWidth = '300px';
        instruction.innerHTML = 'Выделите адрес и скопируйте его вручную<br><br>Нажмите на поле для закрытия';
        
        // Добавляем элементы на страницу
        document.body.appendChild(instruction);
        document.body.appendChild(textArea);
        
        // Фокус на поле ввода
        textArea.focus();
        textArea.select();
        
        // Закрываем по клику на поле или инструкцию
        const closeElements = () => {
          document.body.removeChild(textArea);
          document.body.removeChild(instruction);
        };
        
        textArea.addEventListener('click', closeElements);
        instruction.addEventListener('click', closeElements);
        
        // Автоматически закрываем через 10 секунд
        setTimeout(closeElements, 10000);
      }
    } catch (err) {
      console.error('Ошибка при копировании:', err);
      
      // Последний fallback - показываем адрес для ручного копирования
      alert(`Адрес кошелька: ${walletAddress}\n\nСкопируйте его вручную.`);
    }
  };

  // --- Chat Messages state ---
  const [chatMessages, setChatMessages] = React.useState<Array<{
    id: number;
    sender_type: 'user' | 'bot';
    message: string;
    timestamp_msk: string;
    template_key?: string;
    template_params?: string;
    message_type?: string;
  }>>([]);
  
  // Ref для автоскролла к последнему сообщению
  const chatContainerRef = React.useRef<HTMLDivElement>(null);
  const messagesAbortRef = React.useRef<AbortController | null>(null);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState<boolean>(false);
  const suppressAutoScrollRef = React.useRef<boolean>(false);
  const isAtBottomRef = React.useRef<boolean>(true);
  const loadingRef = React.useRef<boolean>(false);
  const requestIdRef = React.useRef<number>(0);
  const scrollThrottleRef = React.useRef<boolean>(false);
  const topSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const topObserverRef = React.useRef<IntersectionObserver | null>(null);
  const ioCooldownRef = React.useRef<boolean>(false);
  const messageIndexMapRef = React.useRef<Map<number, number>>(new Map());
  const isDebugEnabled = () => (typeof window !== 'undefined' && (window as any).DEBUG_CHAT_SCROLL === true);
  // Хистерезис/антидребезг для детерминированной подгрузки
  const lastTriggeredCursorRef = React.useRef<string | null>(null);
  const hysteresisPassedRef = React.useRef<boolean>(true);
  const lastTriggerAtMsRef = React.useRef<number>(0);

  // Возвращает фактический скролл-контейнер (на случай особенностей мобильных браузеров)
  const getScrollContainer = React.useCallback((): HTMLElement | null => {
    const c = chatContainerRef.current;
    if (!c) return null;
    // Если контейнер реально прокручиваемый — используем его
    if (c.scrollHeight > c.clientHeight) return c;
    // Иначе пробуем document.scrollingElement как резерв
    const se = document.scrollingElement as HTMLElement | null;
    return se || c;
  }, []);



  // Функция для загрузки непрочитанных сообщений
  const loadUnreadMessages = async () => {
    if (!user) return;
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/chat/unread_counts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(user.user_id) })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.unread_counts) {
          setUnreadMessages(data.unread_counts);
          
          // Проверяем, нужно ли загрузить сообщения для открытого чата
          checkAndHandleNewMessages(data.unread_counts);
        }
      }
    } catch (error) {
      console.error('Error loading unread messages:', error);
    }
  };

  // Функция для проверки и обработки новых сообщений в открытом чате
  const checkAndHandleNewMessages = async (unreadCounts: { [botId: string]: number }) => {
    // Проверяем, есть ли новые сообщения для открытого чата
    if (selectedBot && unreadCounts[selectedBot] > 0) {
      try {
        // Загружаем новые сообщения
        await loadChatMessages(selectedBot);
        
        // Отмечаем как прочитанные
        await fetch(`${process.env.REACT_APP_API_URL}/chat/mark_read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            user_id: parseInt(user!.user_id), 
            bot_name: selectedBot 
          })
        });
        
        // Обновляем счетчики непрочитанных сообщений
        await loadUnreadMessages();
        
      } catch (error) {
        console.error('Error handling new messages:', error);
      }
    }
  };

  // Функция для проверки, доскроллил ли пользователь до конца чата
  const checkIfScrolledToBottom = () => {
    if (!chatContainerRef.current || !selectedBot) return;
    
    const container = chatContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Проверяем, доскроллил ли пользователь до конца (с небольшим допуском в 10px)
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    
    return isAtBottom;
  };

  // Функция для отметки сообщений как прочитанные при скролле до конца
  const markMessagesAsReadOnScroll = async () => {
    if (!user || !selectedBot) return;
    
    const isAtBottom = checkIfScrolledToBottom();
    
    if (isAtBottom && unreadMessages[selectedBot] > 0) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/chat/mark_read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            user_id: parseInt(user.user_id), 
            bot_name: selectedBot 
          })
        });
        
        // Обновляем количество непрочитанных сообщений
        loadUnreadMessages();
      } catch (error) {
        console.error('Error marking messages as read on scroll:', error);
      }
    }
  };

  // --- 2FA Handlers ---
  const handleRequestEmailCode = async () => {
    setTwofaLoading(true);
    setEmailCode('');
    setEmailCodeError('');
    setQrData(null);
    setTotpCode('');
    setTotpError('');
    setTwofaSuccess(false);
    setTwofaStep('email_sent'); // СРАЗУ переключаем на форму ввода кода
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/2fa/request_email_code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      // Успех — ничего не делаем, форма уже показана
    } catch (e: any) {
      setEmailCodeError(e.message);
    } finally {
      setTwofaLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    setTwofaLoading(true);
    setEmailCodeError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/2fa/verify_email_code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, code: emailCode })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      setQrData({ secret: data.secret, otpauth_url: data.otpauth_url, qr_b64: data.qr_b64 });
      setTwofaStep('email_verified');
    } catch (e: any) {
      setEmailCodeError(e.message);
    } finally {
      setTwofaLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setTwofaLoading(true);
    setTotpError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/2fa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, secret: qrData?.secret, totp_code: totpCode })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      setTwofaStep('final');
      setTwofaSuccess(true);
      // --- Обновляем twofa_enabled в user и localStorage ---
      if (user) {
        const updatedUser = { ...user, twofa_enabled: 1 };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (e: any) {
      setTotpError(e.message);
    } finally {
      setTwofaLoading(false);
    }
  };

  // --- Password Change Handlers ---
  const handleRequestPwEmailCode = async () => {
    setPwLoading(true);
    setPwError('');
    setPwSuccess('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/password/request_email_code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      setPwStep('code_sent');
    } catch (e: any) {
      setPwError(e.message);
    } finally {
      setPwLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwLoading(true);
    setPwError('');
    setPwSuccess('');
    if (!currentPassword || !newPassword || !repeatNewPassword) {
      setPwError(t('fill_all_fields'));
      setPwLoading(false);
      return;
    }
    if (newPassword !== repeatNewPassword) {
      setPwError(t('passwords_do_not_match'));
      setPwLoading(false);
      return;
    }
    if (newPassword === currentPassword) {
      setPwError(t('new_password_same_as_old'));
      setPwLoading(false);
      return;
    }
    if (!pwEmailCode) {
      setPwError(t('enter_email_code'));
      setPwLoading(false);
      return;
    }
    if (is2FAEnabled && !pw2faCode) {
      setPwError(t('enter_2fa_code'));
      setPwLoading(false);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/password/change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          current_password: currentPassword,
          new_password: newPassword,
          email_code: pwEmailCode,
          totp_code: is2FAEnabled ? pw2faCode : undefined
        })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      setPwSuccess(t('password_changed_successfully'));
      setPwStep('success');
      setCurrentPassword('');
      setNewPassword('');
      setRepeatNewPassword('');
      setPwEmailCode('');
      setPw2faCode('');
    } catch (e: any) {
      setPwError(e.message);
    } finally {
      setPwLoading(false);
    }
  };

  const handleRemindPassword = async () => {
    if (!user?.email) {
      setRemindPasswordMessage('Email пользователя не найден');
      return;
    }

    setRemindPasswordLoading(true);
    setRemindPasswordMessage(null);

    try {
      const API_URL = process.env.REACT_APP_API_URL as string;
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: user.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setRemindPasswordMessage(t('password_reset_sent'));
        setTimeout(() => {
          setRemindPasswordMessage(null);
        }, 3000);
      } else {
        const errorCode = data.detail?.code;
        let errorMessage = data.detail?.message || t('request_error');
        
        // Обрабатываем специфичные ошибки
        if (errorCode === 'rate_limit_exceeded') {
          errorMessage = t('rate_limit_20_min');
        } else if (errorCode === 'password_not_recoverable') {
          errorMessage = t('password_not_recoverable');
        } else if (errorCode === 'user_not_found') {
          errorMessage = t('account_not_registered');
        }
        
        setRemindPasswordMessage(errorMessage);
      }
    } catch (error) {
      setRemindPasswordMessage(t('network_error'));
    } finally {
      setRemindPasswordLoading(false);
    }
  };

  // --- API Keys Handlers ---
  const checkApiKeys = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api_keys/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(user?.user_id || '0') })
      });
      const data = await res.json();
      if (res.ok) {
        setHasApiKeys(data.has_api_keys);
      }
    } catch (e) {
      console.error('Error checking API keys:', e);
    }
  };

  const handleSaveApiKeys = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessKey.trim() || !secretKey.trim()) {
      setApiKeysError(t('fill_all_fields'));
      return;
    }
    if (!accessKey.startsWith('mx')) {
      setApiKeysError(t('access_key_format_error'));
      return;
    }
    
    setApiKeysLoading(true);
    setApiKeysError('');
    setApiKeysSuccess('');
    
    // СРАЗУ обновляем UI - не ждем ответа от бэкенда
    setHasApiKeys(true);
    setAccessKey('');
    setSecretKey('');
    setApiKeysLoading(false);
    
    // Отправляем запрос в фоне
    fetch(`${process.env.REACT_APP_API_URL}/api_keys/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: parseInt(user?.user_id || '0'),
        access_key: accessKey.trim(),
        secret_key: secretKey.trim()
      })
    })
    .then(res => res.json())
    .then(data => {
      if (!data.success) {
        // Если что-то пошло не так, возвращаем UI в исходное состояние
        setHasApiKeys(false);
        setApiKeysError(t('unknown_error'));
      }
    })
    .catch(e => {
      // При ошибке возвращаем UI в исходное состояние
      setHasApiKeys(false);
      setApiKeysError(e.message);
    });
  };

  const handleRevokeApiKeys = () => {
    setShowRevokeConfirm(true);
  };

  const confirmRevokeApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api_keys/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(user?.user_id || '0') })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      setHasApiKeys(false);
      setShowApiKeys(false);
      setApiKeysData(null);
      setApiKeysSuccess(t('api_keys_revoked_successfully'));
    } catch (e: any) {
      setApiKeysError(e.message);
    } finally {
      setApiKeysLoading(false);
      setShowRevokeConfirm(false);
    }
  };

  const cancelRevokeApiKeys = () => {
    setShowRevokeConfirm(false);
  };

  const handleShowApiKeys = async () => {
    if (showApiKeys) {
      // Если ключи уже показаны, скрываем их
      setShowApiKeys(false);
      setApiKeysData(null);
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api_keys/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(user?.user_id || '0') })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      setApiKeysData(data);
      setShowApiKeys(true);
    } catch (e: any) {
      setApiKeysError(e.message);
    }
  };

  React.useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
    } else {
      const parsedUser = JSON.parse(userData);
      // Проверяем, является ли пользователь админом через backend API
      isUserAdmin(parsedUser.user_id, parsedUser.email).then(isAdmin => {
        parsedUser.is_admin = isAdmin;
        // Получаем флаг согласия с дисклеймером (если backend уже отдаёт эти поля, здесь можно заменить на API /me)
        setUser(parsedUser);
        // Если поле не известно, пробуем запросить профиль пользователя (не блокирую, best-effort)
        try {
          const API_URL = process.env.REACT_APP_API_URL as string;
          fetch(`${API_URL}/users/me?user_id=${encodeURIComponent(parsedUser.user_id)}` , {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data && data.user_id) {
              setUser((prev) => ({ ...(prev || parsedUser), disclaimer_accepted: data.disclaimer_accepted, disclaimer_accepted_at: data.disclaimer_accepted_at }));
              // Показываем модал только если пользователь НЕ согласился с дисклеймером
              if (data.disclaimer_accepted !== true) {
                setShowDisclaimerModal(true);
              }
            } else {
              // Если профиль не доступен, покажем модал только если в localStorage нет согласия
              setShowDisclaimerModal(!(parsedUser?.disclaimer_accepted === true));
            }
          })
          .catch(() => {
            setShowDisclaimerModal(!(parsedUser?.disclaimer_accepted === true));
          });
        } catch (_) {
          setShowDisclaimerModal(!(parsedUser?.disclaimer_accepted === true));
        }
      }).catch(error => {
        console.error('Error checking admin status:', error);
        parsedUser.is_admin = false;
        setUser(parsedUser);
        setShowDisclaimerModal(!(parsedUser?.disclaimer_accepted === true));
      });
      return; // Не устанавливаем пользователя здесь, так как это делается в then/catch
    }
  }, [navigate]);
  const handleAcceptDisclaimer = async () => {
    if (!user?.user_id) return;
    try {
      const API_URL = process.env.REACT_APP_API_URL as string;
      const res = await fetch(`${API_URL}/users/${parseInt(user.user_id, 10)}/accept-disclaimer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        setUser((prev) => ({ ...(prev as any), disclaimer_accepted: true, disclaimer_accepted_at: new Date().toISOString() }));
        setShowDisclaimerModal(false);
      }
    } catch (e) {
      console.error('Error accepting disclaimer:', e);
    }
  };



  // Убираем фокус-подсветку у всех ссылок @ForsBotSupport, которые рендерятся через dangerouslySetInnerHTML
  React.useEffect(() => {
    const selector = `a[href="${process.env.REACT_APP_SUPPORT_TELEGRAM_URL}"]`;
    document.querySelectorAll(selector).forEach((el) => {
      el.classList.add('focus:outline-none', 'focus:ring-0', 'active:outline-none', 'active:ring-0');
    });
  }, [chatMessages, i18n.language]);

  React.useEffect(() => {
    if (user?.user_id) {
      checkApiKeys();
      loadUnreadMessages();
    }
  }, [user?.user_id]);







  const languageItems = [
    {
      id: 'eng',
      label: 'English',
      onClick: () => switchLanguage('ENG')
    },
    {
      id: 'rus',
      label: 'Русский',
      onClick: () => switchLanguage('RUS')
    }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home, onClick: () => navigate('/') },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];



  const accountItems = getAccountDropdownItems(t, navigate, user?.is_admin || false);
  const location = useLocation();
  
  // Используем хук для работы с ботами и подписками
  const { myBots, availableBots, loading: botsLoading, error: botsError, refreshSubscriptions } = useBots(user?.user_id ? parseInt(user.user_id) : null);
  

  
  // Все боты (объединяем моих и доступных)
  const allBots = [...myBots, ...availableBots];
  
  // Выбираем первого доступного бота или первого из всех
  const [selectedBot, setSelectedBot] = React.useState<string | null>(null);
  
  React.useEffect(() => {
    // Не автовыбирать бота на мобильных, чтобы не провоцировать фантомы и авто mark_read
    const isMobileNow = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
    if (isMobileNow) return;
    if (!selectedBot) {
      if (myBots.length > 0) {
        setSelectedBot(myBots[0].name);
      } else if (availableBots.length > 0) {
        setSelectedBot(availableBots[0].name);
      }
    }
  }, [myBots, availableBots, selectedBot]);

  // Синхронизация WebSocket сообщений с локальным состоянием
  React.useEffect(() => {
    // Обновляем счетчики непрочитанных из WebSocket
    if (lastUnreadCounts) {
      setUnreadMessages(prev => ({
        ...prev,
        ...lastUnreadCounts
      }));
    }
  }, [lastUnreadCounts]);

  // Обработка новых сообщений из WebSocket
  React.useEffect(() => {
    if (lastNewMessages && selectedBot && lastNewMessages[selectedBot]) {
      // Если есть новые сообщения для текущего бота, загружаем чат
      const newMessages = lastNewMessages[selectedBot];
      if (newMessages.length > 0) {
        // Загружаем сообщения для обновления чата
        loadChatMessages(selectedBot);
      }
    }
  }, [lastNewMessages, selectedBot]);



  // Обработчик скролла для отметки сообщений как прочитанные
  React.useEffect(() => {
    const container = chatContainerRef.current;
    if (!container || !selectedBot) return;

    const handleScroll = () => {
      markMessagesAsReadOnScroll();
    };

    container.addEventListener('scroll', handleScroll);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [selectedBot, unreadMessages, user]);
  
  const isBotAvailable = (botName: string) => {
    return myBots.some(bot => bot.name === botName);
  };
  // Мобильный режим: если открыт чат, показываем только чат
  const [mobileChatOpen, setMobileChatOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Автоматическая загрузка сообщений при выборе бота (только на десктопе)
  React.useEffect(() => {
    if (selectedBot && !isMobile) {
      // Очищаем прежний список, чтобы исключить кратковременный показ старых сообщений
      setChatMessages([]);
      loadChatMessages(selectedBot);
      
      // Отмечаем сообщения как прочитанные при автоматическом выборе бота на десктопе
      if (user) {
        fetch(`${process.env.REACT_APP_API_URL}/chat/mark_read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            user_id: parseInt(user.user_id), 
            bot_name: selectedBot 
          })
        }).then(() => {
          loadUnreadMessages();
        }).catch(error => {
          console.error('Error marking messages as read:', error);
        });
      }
    }
  }, [selectedBot, isMobile, user]);
  const isSettings = location.pathname === '/user/settings';
  
  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;

  // Проверка для PWA: нужно ли применять мобильный отступ
  // (только для устройств с Safe Area - iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');
  const shouldUseMobileOffset = isPWA && hasSafeArea && (isMobile || window.innerHeight < window.innerWidth);
  
  // Проверка для PWA: нужно ли поднять контент в горизонтальной ориентации
  // (только для устройств с Safe Area - iPhone)
  const shouldLiftContent = isPWA && hasSafeArea && !isMobile && window.innerHeight < window.innerWidth;
  
  // Сброс мобильного чата при переходе на "Управление аккаунтом"
  React.useEffect(() => {
    // Если переходим на страницу "Управление аккаунтом" и чат открыт на мобильном
    if (location.pathname === '/user/settings' && mobileChatOpen && isMobile) {
      setMobileChatOpen(false);
      setSelectedBot(null);
    }
  }, [location.pathname, mobileChatOpen, isMobile]);
  
  const [isCommandPanelOpen, setIsCommandPanelOpen] = React.useState(false);
  const [clickedCommandIndex, setClickedCommandIndex] = React.useState<number | null>(null);
  const [isBackButtonClicked, setIsBackButtonClicked] = React.useState(false);
  const chatSectionRef = React.useRef<HTMLDivElement | null>(null);
  const [accordionStyle, setAccordionStyle] = React.useState<React.CSSProperties>({});
  const commandPanelBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const commandAccordionRef = React.useRef<HTMLDivElement | null>(null);
  const [forceRerender, setForceRerender] = React.useState(0);

  // --- Statistics Modal state ---
  const [isStatsModalOpen, setIsStatsModalOpen] = React.useState(false);
  
  // --- Orders Modal state ---
  const [isOrdersModalOpen, setIsOrdersModalOpen] = React.useState(false);
  
  // --- Parameters Modal state ---
  const [isParametersModalOpen, setIsParametersModalOpen] = React.useState(false);
  
  // --- Order Modal state ---
  const [isOrderModalOpen, setIsOrderModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (isCommandPanelOpen && !isMobile && chatSectionRef.current) {
      const rect = chatSectionRef.current.getBoundingClientRect();
      setAccordionStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        bottom: 48,
        zIndex: 50,
        marginBottom: 48
      });
    } else if (isCommandPanelOpen && isMobile) {
      setAccordionStyle({
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 48,
        zIndex: 50
      });
    }
  }, [isCommandPanelOpen, isMobile]);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      // Если клик по кнопке 'Панель команд' — не закрывать аккордеон
      if ((event.target as HTMLElement)?.closest('[data-command-panel-btn]')) {
        return;
      }
      
      const acc = commandAccordionRef.current;
      if (acc && !acc.contains(event.target as Node)) {
        setIsCommandPanelOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isCommandPanelOpen]);

  React.useEffect(() => {
    function handleCloseCommandPanel() {
      setIsCommandPanelOpen(false);
    }
    window.addEventListener('closeCommandPanel', handleCloseCommandPanel);
    return () => {
      window.removeEventListener('closeCommandPanel', handleCloseCommandPanel);
    };
  }, []);

  const commandList = [
    { icon: Rocket, ru: 'Запустить автоторговлю', en: 'Start auto-trading' },
    { icon: Target, ru: 'Разовый ордер', en: 'One-time order' },
    { icon: Calculator, ru: 'Статистика', en: 'Statistics' },
    { icon: Wallet, ru: 'Баланс', en: 'Balance' },
    { icon: DollarSign, ru: 'Текущая цена', en: 'Current price' },
    { icon: Boxes, ru: 'Открытые ордера', en: 'Open orders' },
    { icon: Settings, ru: 'Параметры', en: 'Parameters' },
    { icon: Calendar, ru: 'Подписка', en: 'Subscription' },
    { icon: StopCircle, ru: 'Остановить автоторговлю', en: 'Stop auto-trading' },
    { icon: HelpCircle, ru: 'Помощь', en: 'Help' },
  ];

  const handleCommandClick = async (index: number) => {
    setClickedCommandIndex(index);
    
    if (!user || !selectedBot) return;
    
    // Получаем команду по индексу
    const command = commandList[index];
    const commandText = i18n.language === 'ENG' ? command.en : command.ru;
    
    try {
      // Специальная обработка для команды "Статистика" (индекс 2)
      if (index === 2) { // Статистика
        setIsStatsModalOpen(true);
        // Закрываем аккордеон команд и сбрасываем подсветку
        setTimeout(() => {
          setClickedCommandIndex(null);
          setIsCommandPanelOpen(false);
        }, 200);
        return;
      }
      
      // Специальная обработка для команды "Разовый ордер" (индекс 1)
      if (index === 1) { // Разовый ордер
        setIsOrderModalOpen(true);
        // Закрываем аккордеон команд и сбрасываем подсветку
        setTimeout(() => {
          setClickedCommandIndex(null);
          setIsCommandPanelOpen(false);
        }, 200);
        return;
      }
      
      // Специальная обработка для команды "Открытые ордера" (индекс 5)
      if (index === 5) { // Открытые ордера
        setIsOrdersModalOpen(true);
        // Закрываем аккордеон команд и сбрасываем подсветку
        setTimeout(() => {
          setClickedCommandIndex(null);
          setIsCommandPanelOpen(false);
        }, 200);
        return;
      }
      
      // Специальная обработка для команды "Параметры" (индекс 6)
      if (index === 6) { // Параметры
        setIsParametersModalOpen(true);
        // Закрываем аккордеон команд и сбрасываем подсветку
        setTimeout(() => {
          setClickedCommandIndex(null);
          setIsCommandPanelOpen(false);
        }, 200);
        return;
      }
      
      // Специальная обработка для команды "Помощь" (индекс 9)
      if (index === 9) { // Помощь
        // Для команды "Помощь" не отправляем сообщение пользователя
        const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: parseInt(user.user_id),
            bot_name: selectedBot,
            sender_type: 'bot',
            message: '', // Пустое сообщение, будет переведено через шаблон
            template_key: 'help_info',
            template_params: {},
            message_type: 'bot_response'
          })
        });

        if (botResponse.ok) {
          // Обновляем счетчик непрочитанных сообщений
          setUnreadMessages(prev => ({
            ...prev,
            [selectedBot]: (prev[selectedBot] || 0) + 1
          }));
          // Перезагружаем сообщения
          loadChatMessages(selectedBot);
          // Автоскролл к последнему сообщению
          setTimeout(scrollToBottom, 200);
        }
      } else if (index === 0) { // Запустить автоторговлю
        try {
          // Используем SPA бэкенд для запуска автоторговли
          const autobuyResponse = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/autobuy/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot
            })
          });

          if (autobuyResponse.ok) {
            const result = await autobuyResponse.json();
            
            if (result.success) {
              // Торговый бот уже отправил уведомление об успешном запуске
              // Обновляем счетчик непрочитанных сообщений
              setUnreadMessages(prev => ({
                ...prev,
                [selectedBot]: (prev[selectedBot] || 0) + 1
              }));
              // Перезагружаем сообщения
              loadChatMessages(selectedBot);
              // Автоскролл к последнему сообщению
              setTimeout(scrollToBottom, 200);
            } else {
              // Торговый бот уже отправил уведомление об ошибке
              // Обновляем счетчик непрочитанных сообщений
              setUnreadMessages(prev => ({
                ...prev,
                [selectedBot]: (prev[selectedBot] || 0) + 1
              }));
              // Перезагружаем сообщения
              loadChatMessages(selectedBot);
              setTimeout(scrollToBottom, 200);
            }
          } else {
            // В случае HTTP ошибки торговый бот недоступен
            // Обновляем счетчик непрочитанных сообщений
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            // Перезагружаем сообщения
            loadChatMessages(selectedBot);
            setTimeout(scrollToBottom, 200);
          }
        } catch (error) {
          console.error('Error starting autobuy:', error);
          // В случае исключения торговый бот недоступен
          // Обновляем счетчик непрочитанных сообщений
          setUnreadMessages(prev => ({
            ...prev,
            [selectedBot]: (prev[selectedBot] || 0) + 1
          }));
          // Перезагружаем сообщения
          loadChatMessages(selectedBot);
          setTimeout(scrollToBottom, 200);
        }
      } else if (index === 3) { // Баланс
        // Убираем отправку сообщения пользователя - показываем только ответ бота
        // Сразу получаем реальный баланс из торгового бота
        try {
          const balanceResponse = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/get_balance`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot
            })
          });

          let balanceData = null;
          if (balanceResponse.ok) {
            const balanceResult = await balanceResponse.json();
            if (balanceResult.success) {
              balanceData = balanceResult.balance_data;
            }
          }

          // Получаем маппинг бота для правильного определения валют
          const botMapping = getBotMapping(selectedBot);
          
          // Определяем параметры для шаблона на основе реальных данных или значений по умолчанию
          const templateParams = {
            quote_currency: botMapping.quoteCurrency,
            quote_free: balanceData?.balance?.free?.[botMapping.quoteCurrency]?.toString() || '0.00',
            quote_used: balanceData?.balance?.used?.[botMapping.quoteCurrency]?.toString() || '0.00',
            coin: botMapping.baseCoin,
            coin_free: balanceData?.balance?.free?.[botMapping.baseCurrency]?.toString() || '0.00',
            coin_used: balanceData?.balance?.used?.[botMapping.baseCurrency]?.toString() || '0.00',
            orders_count: balanceData?.pending_orders?.total?.toString() || '0',
            pending_amount: balanceData?.pending_orders?.pending_quote?.toString() || '0.00',
            avg_price: balanceData?.pending_orders?.avg_price?.toString() || '0.00'
          };



          // Отправляем ответ бота с реальными данными
          const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot,
              sender_type: 'bot',
              message: '', // Пустое сообщение, будет переведено через шаблон
              template_key: 'balance_info',
              template_params: templateParams,
              message_type: 'bot_response'
            })
          });

          if (botResponse.ok) {
            // Обновляем счетчик непрочитанных сообщений
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            // Перезагружаем сообщения
            loadChatMessages(selectedBot);
            // Автоскролл к последнему сообщению
            setTimeout(scrollToBottom, 200);
          }
        } catch (error) {
          console.error('Error getting balance:', error);
          // В случае ошибки отправляем сообщение об ошибке
          const errorResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot,
              sender_type: 'bot',
              message: 'Ошибка получения баланса. Попробуйте позже.',
              template_key: "",
              template_params: {},
              message_type: 'bot_response'
            })
          });

          if (errorResponse.ok) {
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            loadChatMessages(selectedBot);
            setTimeout(scrollToBottom, 200);
          }
        }
      } else if (index === 4) { // Текущая цена
        try {
          // Получаем маппинг бота для правильного определения bot_id и торговой пары
          const botMapping = getBotMapping(selectedBot);
          const botId = botMapping.botId;
          const tradingPair = botMapping.tradingPair;
          

          
          // Получаем реальную цену из торгового бота
          const TRADING_BOT_URL = process.env.REACT_APP_TRADING_BOT_URL as string;
          const priceResponse = await fetch(`${TRADING_BOT_URL}/api/bots/${botId}/price?pair=${tradingPair}`, {
            method: 'GET',
            headers: { 
              'X-API-Key': process.env.REACT_APP_TRADING_BOT_API_KEY || '',
              'Content-Type': 'application/json'
            }
          });

          let currentPrice = '0.000000'; // Значение по умолчанию
          
          if (priceResponse.ok) {
            const priceData = await priceResponse.json();
            currentPrice = priceData.price?.toString() || '0.000000';

          } else {
            console.warn(`Не удалось получить цену для ${selectedBot} из торгового бота:`, priceResponse.status);
          }

          // Отправляем сообщение с реальной ценой
          const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot,
              sender_type: 'bot',
              message: '', // Пустое сообщение, будет переведено через шаблон
              template_key: 'price_info',
              template_params: {
                coin: botMapping.baseCoin,
                price: currentPrice,
                quote_currency: botMapping.quoteCurrency
              },
              message_type: 'bot_response'
            })
          });

          if (botResponse.ok) {
            // Обновляем счетчик непрочитанных сообщений
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            // Перезагружаем сообщения
            loadChatMessages(selectedBot);
            // Автоскролл к последнему сообщению
            setTimeout(scrollToBottom, 200);
          }
        } catch (error) {
          console.error('Ошибка при получении цены:', error);
          // В случае ошибки отправляем сообщение с ценой по умолчанию
          const botMapping = getBotMapping(selectedBot);
          const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot,
              sender_type: 'bot',
              message: '', // Пустое сообщение, будет переведено через шаблон
              template_key: 'price_info',
              template_params: {
                coin: botMapping.baseCoin,
                price: '0.000000',
                quote_currency: botMapping.quoteCurrency
              },
              message_type: 'bot_response'
            })
          });

          if (botResponse.ok) {
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            loadChatMessages(selectedBot);
            setTimeout(scrollToBottom, 200);
          }
        }
      } else if (index === 7) { // Подписка
        // Для команды "Подписка" не отправляем сообщение пользователя
        try {
          // Получаем статус подписки
          const subscriptionResponse = await fetch(`${process.env.REACT_APP_API_URL}/subscription/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot
            })
          });

          if (subscriptionResponse.ok) {
            const subscriptionData = await subscriptionResponse.json();
            const subscriptionStatus = subscriptionData.subscription_status;

            // Определяем шаблон и параметры в зависимости от статуса подписки
            let templateKey = 'subscription_active';
            let templateParams = {};

            // Определяем язык для форматирования даты
            const dateLanguage = currentLanguage === 'ENG' ? 'ENG' : 'RUS';
            
            // Показываем информацию только для активной подписки
            if (subscriptionStatus.has_subscription && subscriptionStatus.is_active) {
              templateKey = 'subscription_active';
              templateParams = {
                end_date: subscriptionStatus.subscription_until, // Сохраняем сырую дату
                days_remaining: subscriptionStatus.days_remaining
              };
            } else {
              // Если подписка истекла или нет подписки, не показываем сообщение
              return;
            }

            // Отправляем ответ бота
            const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: parseInt(user.user_id),
                bot_name: selectedBot,
                sender_type: 'bot',
                message: '', // Пустое сообщение, будет переведено через шаблон
                template_key: templateKey,
                template_params: templateParams,
                message_type: 'bot_response'
              })
            });

            if (botResponse.ok) {
              // Обновляем счетчик непрочитанных сообщений
              setUnreadMessages(prev => ({
                ...prev,
                [selectedBot]: (prev[selectedBot] || 0) + 1
              }));
              // Перезагружаем сообщения
              loadChatMessages(selectedBot);
              // Автоскролл к последнему сообщению
              setTimeout(scrollToBottom, 200);
            }
          }
        } catch (error) {
          console.error('Error getting subscription status:', error);
        }
      } else if (index === 8) { // Остановить автоторговлю
        try {
          // Используем SPA бэкенд для остановки автоторговли
          const autobuyResponse = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/autobuy/stop`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot
            })
          });

          if (autobuyResponse.ok) {
            const result = await autobuyResponse.json();
            
            if (result.success) {
              // Торговый бот уже отправил уведомление об остановке
              // Обновляем счетчик непрочитанных сообщений
              setUnreadMessages(prev => ({
                ...prev,
                [selectedBot]: (prev[selectedBot] || 0) + 1
              }));
              // Перезагружаем сообщения
              loadChatMessages(selectedBot);
              // Автоскролл к последнему сообщению
              setTimeout(scrollToBottom, 200);
            } else {
              // В случае ошибки отправляем сообщение об ошибке
              const errorResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  user_id: parseInt(user.user_id),
                  bot_name: selectedBot,
                  sender_type: 'bot',
                  message: result.message || 'Ошибка остановки автоторговли',
                  template_key: "",
                  template_params: {},
                  message_type: 'bot_response'
                })
              });

              if (errorResponse.ok) {
                setUnreadMessages(prev => ({
                  ...prev,
                  [selectedBot]: (prev[selectedBot] || 0) + 1
                }));
                loadChatMessages(selectedBot);
                setTimeout(scrollToBottom, 200);
              }
            }
          } else {
            // В случае ошибки HTTP отправляем сообщение об ошибке
            const errorResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: parseInt(user.user_id),
                bot_name: selectedBot,
                sender_type: 'bot',
                message: 'Ошибка подключения к торговому боту',
                template_key: "",
                template_params: {},
                message_type: 'bot_response'
            })
            });

            if (errorResponse.ok) {
              setUnreadMessages(prev => ({
                ...prev,
                [selectedBot]: (prev[selectedBot] || 0) + 1
              }));
              loadChatMessages(selectedBot);
              setTimeout(scrollToBottom, 200);
            }
          }
        } catch (error) {
          console.error('Error stopping autobuy:', error);
          // В случае исключения отправляем сообщение об ошибке
          const errorResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot,
              sender_type: 'bot',
              message: 'Ошибка остановки автоторговли',
              template_key: "",
              template_params: {},
              message_type: 'bot_response'
            })
          });

          if (errorResponse.ok) {
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            loadChatMessages(selectedBot);
            setTimeout(scrollToBottom, 200);
          }
        }
      } else {
        // Для остальных команд - стандартная логика
        // Отправляем сообщение пользователя
        const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: parseInt(user.user_id),
            bot_name: selectedBot,
            sender_type: 'user',
            message: commandText
          })
        });

        if (userResponse.ok) {
          // Отправляем ответ бота (заглушка)
          const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot,
              sender_type: 'bot',
              message: `Торговый бот offline`
            })
          });

          if (botResponse.ok) {
            // Обновляем счетчик непрочитанных сообщений
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            // Перезагружаем сообщения
            loadChatMessages(selectedBot);
            // Автоскролл к последнему сообщению
            setTimeout(scrollToBottom, 200);
          }
        }
      }
    } catch (error) {
      console.error('Error sending command:', error);
    }
    
    // Убираем подсветку через 200мс
    setTimeout(() => {
      setClickedCommandIndex(null);
      setIsCommandPanelOpen(false);
    }, 200);
  };

  // --- Statistics handlers ---
  const handleStatsPeriodSelect = async (period: 'day' | 'month' | 'year' | 'all') => {
    if (!user || !selectedBot) return;

    try {
      // Определяем дату в зависимости от периода
      let date: string | undefined;
      
      if (period === 'day') {
        date = new Date().toLocaleDateString('en-CA'); // Сегодня в локальном времени
      } else if (period === 'month') {
        const now = new Date();
        date = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      } else if (period === 'year') {
        date = new Date().getFullYear().toString();
      }
      // Для 'all' date остается undefined

      // Отправляем запрос к trading bot через BackendSPA
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/get_stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot,
          period: period,
          date: date,
          language: currentLanguage === 'ENG' ? 'en' : 'ru'
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const statsData = result.stats_data;
          // Отправляем сообщение в чат с результатами статистики
          const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot,
              sender_type: 'bot',
              message: '', // Пустое сообщение, будет переведено через шаблон
              template_key: 'stats_info',
              template_params: {
                period_display: statsData.period_display,
                count: statsData.count,
                profit: statsData.profit,
                currency: statsData.currency
              },
              message_type: 'bot_response'
            })
          });

          if (botResponse.ok) {
            // Обновляем счетчик непрочитанных сообщений
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            // Перезагружаем сообщения
            loadChatMessages(selectedBot);
            // Автоскролл к последнему сообщению
            setTimeout(scrollToBottom, 200);
          }
        }
      }
    } catch (error) {
      console.error('Error getting statistics:', error);
    }
  };

  const handleStatsDateSelect = async (date: string) => {
    if (!user || !selectedBot) return;

    try {
      // Определяем период на основе формата даты
      let period: 'day' | 'month' | 'year';
      
      if (date.includes('-') && date.split('-').length === 3) {
        period = 'day'; // YYYY-MM-DD
      } else if (date.includes('-') && date.split('-').length === 2) {
        period = 'month'; // YYYY-MM
      } else {
        period = 'year'; // YYYY
      }

      // Отправляем запрос к trading bot через BackendSPA
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/get_stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot,
          period: period,
          date: date,
          language: currentLanguage === 'ENG' ? 'en' : 'ru'
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const statsData = result.stats_data;
          // Отправляем сообщение в чат с результатами статистики
          const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              bot_name: selectedBot,
              sender_type: 'bot',
              message: '', // Пустое сообщение, будет переведено через шаблон
              template_key: 'stats_info',
              template_params: {
                period_display: statsData.period_display,
                count: statsData.count,
                profit: statsData.profit,
                currency: statsData.currency
              },
              message_type: 'bot_response'
            })
          });

          if (botResponse.ok) {
            // Обновляем счетчик непрочитанных сообщений
            setUnreadMessages(prev => ({
              ...prev,
              [selectedBot]: (prev[selectedBot] || 0) + 1
            }));
            // Перезагружаем сообщения
            loadChatMessages(selectedBot);
            // Автоскролл к последнему сообщению
            setTimeout(scrollToBottom, 200);
          }
        }
      }
    } catch (error) {
      console.error('Error getting statistics:', error);
    }
  };

  // --- Order handlers ---
  const handleOrderCreated = async (orderData: any) => {
    if (!user || !selectedBot) return;

    try {
      // Определяем тип шаблона на основе типа ордера
      const templateKey = orderData.type === 'buy' ? 'order_buy_executed' : 'order_sell_executed';
      
      // Отправляем сообщение в чат с результатом ордера
      const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot,
          sender_type: 'bot',
          message: '', // Пустое сообщение, будет переведено через шаблон
          template_key: templateKey,
          template_params: orderData.template_params,
          message_type: 'bot_response'
        })
      });

      if (botResponse.ok) {
        // Обновляем счетчик непрочитанных сообщений
        setUnreadMessages(prev => ({
          ...prev,
          [selectedBot]: (prev[selectedBot] || 0) + 1
        }));
        // Перезагружаем сообщения
        loadChatMessages(selectedBot);
        // Автоскролл к последнему сообщению
        setTimeout(scrollToBottom, 200);
      }
    } catch (error) {
      console.error('Error sending order result message:', error);
    }
  };

  // --- Parameters handlers ---
  const handleParametersUpdated = async (params: any) => {
    if (!user || !selectedBot) return;

    try {
      // Отправляем сообщение в чат с обновленными параметрами
      const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/chat/send_message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot,
          sender_type: 'bot',
          message: '', // Пустое сообщение, будет переведено через шаблон
          template_key: 'parameters_updated',
          template_params: {
            buy_amount: params.buy_amount,
            profit_percent: params.profit_percent,
            auto_buy_up_interval: params.auto_buy_up_interval,
            auto_buy_down_percent: params.auto_buy_down_percent,
            fee_percent: params.fee_percent
          },
          message_type: 'bot_response'
        })
      });

      if (botResponse.ok) {
        // Обновляем счетчик непрочитанных сообщений
        setUnreadMessages(prev => ({
          ...prev,
          [selectedBot]: (prev[selectedBot] || 0) + 1
        }));
        // Перезагружаем сообщения
        loadChatMessages(selectedBot);
        // Автоскролл к последнему сообщению
        setTimeout(scrollToBottom, 200);
      }
    } catch (error) {
      console.error('Error sending parameters update message:', error);
    }
  };

  const handleBackButtonClick = () => {
    setIsBackButtonClicked(true);
    // Убираем подсветку через 200мс
    setTimeout(() => {
      setIsBackButtonClicked(false);
      if (isCommandPanelOpen) {
        setIsCommandPanelOpen(false);
        setTimeout(() => {
          setMobileChatOpen(false);
          setSelectedBot(null);
        }, 0);
      } else {
        setMobileChatOpen(false);
        setSelectedBot(null);
      }
    }, 200);
  };

  // Функция для автоскролла к последнему сообщению
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Функция для загрузки сообщений чата
  const loadChatMessages = async (botName: string) => {
    if (!user) return;
    
    try {
      // Отменяем предыдущий запрос, чтобы избежать гонок при быстром переключении ботов
      (messagesAbortRef.current)?.abort();
      const controller = new AbortController();
      messagesAbortRef.current = controller;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: botName,
          limit: 100
        }),
        signal: controller.signal
      });

      if (response.ok) {
        const data = await response.json();
        // Сообщения уже приходят в правильном порядке (новые снизу)
        setChatMessages(data.messages || []);
        // Сохраняем курсор и флаг для последующей подгрузки вверх
        setNextCursor(data?.next_cursor ?? null);
        setHasMore(Boolean(data?.has_more));
        // Автоскролл к последнему сообщению после загрузки (первичный вход — всегда вниз)
        setTimeout(() => {
          isAtBottomRef.current = true;
          scrollToBottom();
        }, 100);
        
        // Если пользователь уже внизу чата, отмечаем сообщения как прочитанные
        setTimeout(() => {
          if (checkIfScrolledToBottom()) {
            markMessagesAsReadOnScroll();
          }
        }, 200);
      } else {
        console.error('Error loading chat messages:', response.status, response.statusText);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      console.error('Error loading chat messages:', error);
    }
  };

  // Догрузка старых сообщений при прокрутке вверх
  const loadOlderMessages = React.useCallback(async () => {
    if (!user || !selectedBot || !hasMore || isLoadingMore || !nextCursor) return;
    if (loadingRef.current) return;
    loadingRef.current = true;
    const container = getScrollContainer();
    if (!container) return;
    try {
      // Подавляем автоскролл вниз из эффектов на время догрузки вверх
      suppressAutoScrollRef.current = true;
      // На время загрузки отключаем IO, чтобы не было повторных триггеров
      try { topObserverRef.current?.disconnect(); } catch {}
      setIsLoadingMore(true);
      const reqId = ++requestIdRef.current;
      // Определяем первый реально видимый элемент (верхний) внутри контейнера — в координатах контейнера
      let anchorEl: HTMLElement | null = null;
      const containerRect = container.getBoundingClientRect();
      // Пытаемся найти первый видимый элемент точкой внутри контейнера
      const probeX = Math.round(containerRect.left + 10);
      const probeY = Math.round(containerRect.top + 10);
      let probeEl = document.elementFromPoint(probeX, probeY) as HTMLElement | null;
      const findMessageAncestor = (el: HTMLElement | null): HTMLElement | null => {
        let cur = el;
        while (cur && cur !== container) {
          if (cur.classList && cur.classList.contains('chat-message')) return cur;
          cur = cur.parentElement as HTMLElement | null;
        }
        return null;
      };
      anchorEl = findMessageAncestor(probeEl);
      // Fallback: ищем по списку кандидатов
      if (!anchorEl) {
        const candidates = Array.from(container.querySelectorAll('.chat-message')) as HTMLElement[];
        let bestScreenDist = Number.POSITIVE_INFINITY;
        for (const el of candidates) {
          const elRect = el.getBoundingClientRect();
          const screenDist = elRect.top - containerRect.top;
          if (screenDist >= -0.5 && screenDist < bestScreenDist) {
            bestScreenDist = screenDist;
            anchorEl = el;
          }
        }
        if (!anchorEl && candidates.length > 0) {
          anchorEl = candidates[0];
        }
      }
      const anchorId = anchorEl?.getAttribute('id') || null;
      // Позиция якоря в координатах контейнера до догрузки
      const anchorTopInContainer = anchorEl
        ? (anchorEl.getBoundingClientRect().top - containerRect.top + container.scrollTop)
        : 0;
      // Экранная дистанция якоря (внутри контейнера), которую нужно сохранить
      const anchorScreenDist = anchorEl
        ? (anchorEl.getBoundingClientRect().top - containerRect.top)
        : 0;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot,
          limit: 100,
          before_ts: nextCursor
        })
      });
      if (response.ok) {
        const data = await response.json();
        // Игнорируем устаревший ответ
        if (reqId !== requestIdRef.current) return;
        const older = Array.isArray(data?.messages) ? data.messages : [];
        if (older.length > 0) {
          setChatMessages((prev) => {
            const existing = new Set(prev.map(m => m.id));
            const dedupedOlder = older.filter((m: any) => !existing.has(m.id));
            return dedupedOlder.length ? [...dedupedOlder, ...prev] : prev;
          });
          setNextCursor(data?.next_cursor ?? null);
          setHasMore(Boolean(data?.has_more));
          // Восстанавливаем позицию по якорю относительно контейнера (double rAF + короткая задержка)
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(() => {
                if (anchorId) {
                  const currentAnchor = document.getElementById(anchorId) as HTMLElement | null;
                  if (currentAnchor && container) {
                    const currentTopInContainer = currentAnchor.getBoundingClientRect().top - containerRect.top + container.scrollTop;
                    // Устанавливаем scrollTop так, чтобы визуальная дистанция до верхней границы контейнера сохранилась
                    container.scrollTop = currentTopInContainer - anchorScreenDist;
                  }
                }
                // Небольшой cooldown, чтобы IO не срабатывал повторно сразу после восстановления позиции
                ioCooldownRef.current = true;
                setTimeout(() => { ioCooldownRef.current = false; }, 250);
                // Возобновляем наблюдение IO
                try {
                  if (topSentinelRef.current) {
                    const rootMargin = '100px 0px 0px 0px';
                    topObserverRef.current = new IntersectionObserver((entries) => {
                      for (const entry of entries) {
                        if (entry.isIntersecting && !ioCooldownRef.current && !loadingRef.current) {
                          loadOlderMessages();
                        }
                      }
                    }, {
                      root: container === document.scrollingElement ? null : container,
                      rootMargin,
                      threshold: 0,
                    });
                    topObserverRef.current.observe(topSentinelRef.current);
                  }
                } catch {}
              }, 16);
            });
          });
        } else {
          setHasMore(false);
        }
      } else {
        console.error('Error loading older messages:', response.status, response.statusText);
      }
    } catch (e) {
      console.error('Error loading older messages:', e);
    } finally {
      setIsLoadingMore(false);
      // Разрешаем автоскролл после восстановления позиции
      setTimeout(() => {
        suppressAutoScrollRef.current = false;
        loadingRef.current = false;
      }, 0);
    }
  }, [user, selectedBot, hasMore, isLoadingMore, nextCursor, getScrollContainer]);

  // Хук на скролл: упреждающая подгрузка при достижении порога сверху
  const handleScroll = React.useCallback(() => {
    if (scrollThrottleRef.current) return;
    scrollThrottleRef.current = true;
    setTimeout(() => { scrollThrottleRef.current = false; }, 120);
    const visual = chatContainerRef.current;
    if (!visual) return;
    const scrollTop = visual.scrollTop;
    const isAtBottomNow = visual.scrollTop + visual.clientHeight >= visual.scrollHeight - 10;
    isAtBottomRef.current = isAtBottomNow;
    // Резерв на упоре: если прямо у верхней границы (учитываем паддинги/границы)
    if (scrollTop <= 48 && hasMore && !loadingRef.current) {
      if (isDebugEnabled()) console.log('[CHAT] Top reserve trigger loadOlderMessages', { scrollTop, hasMore, loading: loadingRef.current });
      loadOlderMessages();
      return;
    }
    // Упреждающая подгрузка: как только первый видимый индекс <= 20 — грузим
    try {
      const visualRect = visual.getBoundingClientRect();
      // Определяем первый видимый .chat-message по положению в визуальном контейнере
      const candidates = Array.from(visual.querySelectorAll('.chat-message')) as HTMLElement[];
      let bestIdx: number | null = null;
      let bestTop = Number.POSITIVE_INFINITY;
      for (const el of candidates) {
        const topInVisual = el.getBoundingClientRect().top - visualRect.top;
        if (topInVisual >= -4 && topInVisual < bestTop) {
          const idAttr = el.getAttribute('id');
          const idNum = idAttr && idAttr.startsWith('msg-') ? parseInt(idAttr.slice(4), 10) : NaN;
          if (!Number.isNaN(idNum)) {
            const idx = messageIndexMapRef.current.get(idNum);
            if (typeof idx === 'number') {
              bestTop = topInVisual;
              bestIdx = idx;
            }
          }
        }
      }
      if (isDebugEnabled()) console.log('[CHAT] firstVisibleIdx', bestIdx, { scrollTop, nextCursor });
      // Хистерезис: выходим из зоны, когда индекс достаточно большой
      if (bestIdx !== null && bestIdx >= 40 && !loadingRef.current) {
        hysteresisPassedRef.current = true;
      }
      const now = Date.now();
      const canTrigger = (
        bestIdx !== null &&
        bestIdx <= 20 &&
        hasMore &&
        !loadingRef.current &&
        hysteresisPassedRef.current &&
        typeof nextCursor === 'string' && (nextCursor as string).length > 0 &&
        lastTriggeredCursorRef.current !== nextCursor &&
        (now - lastTriggerAtMsRef.current) >= 500
      );
      if (canTrigger) {
        if (isDebugEnabled()) console.log('[CHAT] Preload trigger: idx<=20 & hysteresis ok');
        lastTriggeredCursorRef.current = nextCursor as string;
        hysteresisPassedRef.current = false;
        lastTriggerAtMsRef.current = now;
        loadOlderMessages();
      }
    } catch {}
  }, [loadOlderMessages, hasMore]);

  React.useEffect(() => {
    const visual = chatContainerRef.current;
    if (!visual) return;
    visual.addEventListener('scroll', handleScroll, { passive: true } as any);
    return () => {
      try { visual.removeEventListener('scroll', handleScroll as any); } catch {}
    };
  }, [handleScroll]);

  // Автоскролл при изменении сообщений и отметка как прочитанные
  React.useEffect(() => {
    if (chatMessages.length > 0) {
      // Если догружаем вверх — не скроллим вниз
      if (!suppressAutoScrollRef.current) {
        // Скроллим вниз только если пользователь внизу
        if (checkIfScrolledToBottom() || isAtBottomRef.current) {
          scrollToBottom();
        }
      }
      
      // Если пользователь уже внизу чата, отмечаем новые сообщения как прочитанные
      setTimeout(() => {
        if (checkIfScrolledToBottom()) {
          markMessagesAsReadOnScroll();
        }
      }, 100);
    }
  }, [chatMessages]);

  // Обновляем карту индексов сообщений для быстрого вычисления firstVisibleIdx
  React.useEffect(() => {
    const indexMap = new Map<number, number>();
    for (let i = 0; i < chatMessages.length; i++) {
      const msg = chatMessages[i] as any;
      const mid = msg?.id;
      if (typeof mid === 'number') {
        indexMap.set(mid, i);
      }
    }
    messageIndexMapRef.current = indexMap;
  }, [chatMessages]);

  // Функция для сброса счетчика непрочитанных сообщений
  const handleBotSelect = async (botName: string) => {
    setSelectedBot(botName);
    setMobileChatOpen(true);
    
    // Загружаем сообщения для выбранного бота
    setChatMessages([]);
    // Сбрасываем состояние пагинации и наблюдателей
    setNextCursor(null);
    setHasMore(false);
    loadingRef.current = false;
    requestIdRef.current = 0;
    try { topObserverRef.current?.disconnect(); } catch {}
    ioCooldownRef.current = false;
    loadChatMessages(botName);
    
    // Отмечаем сообщения как прочитанные при выборе бота
    if (user) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/chat/mark_read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            user_id: parseInt(user.user_id), 
            bot_name: botName 
          })
        });
        
        // Обновляем количество непрочитанных сообщений
        loadUnreadMessages();
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    }
  };

  // Функция для получения иконок обеих валют в паре
  // Используем общую утилиту для получения иконок ботов

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      {showDisclaimerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative z-10 w-full max-w-xl bg-header border border-border rounded-lg shadow-2xl">
            {/* Header */}
            <div className="pl-2 pr-1 py-2 border-b border-border">
              <div className="grid grid-cols-7 gap-1">
                <div></div>
                <div className="col-span-5 flex items-center justify-center">
                  <h2 className="text-lg font-semibold text-white">{t('user_disclaimer_title')}</h2>
                </div>
                <div></div>
              </div>
            </div>
            {/* Content */}
            <div className="p-4 text-white">
              <div className="mb-2 text-sm md:text-base font-medium">{t('user_disclaimer_intro')}</div>
              <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                <li>{t('user_disclaimer_point1')}</li>
                <li>{t('user_disclaimer_point2')}</li>
                <li>{t('user_disclaimer_point3')}</li>
                <li>{t('user_disclaimer_point4')}</li>
                <li>{t('user_disclaimer_point5')}</li>
              </ul>
              {/* Buttons */}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/faq')}
                  className="flex-1 p-3 bg-header border border-border text-white rounded-lg transition-colors focus:outline-none focus:ring-0"
                >
                  {t('user_disclaimer_view_faq')}
                </button>
                <button
                  type="button"
                  onClick={handleAcceptDisclaimer}
                  className="flex-1 p-3 bg-header border border-border text-white rounded-lg transition-colors focus:outline-none focus:ring-0"
                >
                  {t('user_disclaimer_agree')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
          </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
              {/* WebSocket статус убран - теперь показывается только в админ панели */}
              <Dropdown
                id="account"
                trigger={<User className="w-5 h-5 text-iconPrimary" />}
                items={accountItems}
                align="right"
              />
              <Dropdown
                id="language"
                trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>}
                items={languageItems}
                align="right"
              />
              <Dropdown
                id="pages"
                trigger={<Menu className="w-5 h-5 text-iconPrimary" />}
                items={pagesItems}
                align="right"
              />
          </div>
        </div>
      </header>
      <main
        className={`w-full flex-1 flex flex-row min-h-screen items-stretch gap-4 md:gap-8 ${isSettings ? 'py-8 px-4 sm:px-6 lg:px-8' : ''}`}
        style={isSettings ? 
          { 
            paddingTop: (isPWA && hasSafeArea) ? `calc(5rem + ${safeAreaTop})` : '5rem',
            paddingBottom: '0.5cm', 
            paddingLeft: '0.5cm', 
            paddingRight: '0.5cm' 
          } : 
          { 
            paddingTop: (isPWA && hasSafeArea) ? `calc(5rem + 0.5cm + ${safeAreaTop})` : 'calc(5rem + 0.5cm)', 
            paddingBottom: '0.5cm', 
            paddingLeft: '0.5cm', 
            paddingRight: '0.5cm',
            marginTop: shouldLiftContent ? '-1cm' : '0'
          }
        }
      >
        {isSettings ? (
          <section className="mb-8 md:mb-16 max-w-md w-full mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-text mb-4 md:mb-8 font-sans flex items-center justify-center gap-2 mt-8">
              <WrenchIcon className="w-7 h-7 text-primary" />
              {t('manage_account')}
            </h2>
            <div className="bg-header border border-border rounded-lg p-3 md:p-6 flex flex-col gap-4">
              {/* 2FA Accordion */}
              <button
                className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openAccordion === '2fa' ? 'shadow-lg button-glow' : ''}`}
                onClick={() => handleAccordionToggle('2fa')}
                disabled={is2FAEnabled}
              >
                <span className="flex items-center gap-2">
                  {t('twofa')}
                  {is2FAEnabled && <Check className="w-5 h-5 text-green-500 ml-1" />}
                </span>
                {!is2FAEnabled && (
                  <ChevronDown className={`w-5 h-5 transition-transform ${openAccordion === '2fa' ? 'rotate-180' : ''}`} />
                )}
              </button>
              {is2FAEnabled && (
                <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in">
                  <div className="text-green-600 font-semibold text-center">
                    {t('2fa_already_enabled')}
                  </div>
                </div>
              )}
              {openAccordion === '2fa' && !is2FAEnabled && (
                <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in">
                  {/* Пошаговая форма настройки 2FA */}
                  {twofaStep === 'init' && (
                    <div className="flex flex-col gap-3">
                      <button
                          className="px-4 py-2 rounded-lg border border-border text-white font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0 button-glow"
                          style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                        onClick={handleRequestEmailCode}
                        disabled={twofaLoading}
                      >
                        {t('twofa_request_email_code')}
                      </button>
                      {emailCodeError && <div className="text-red-500">{emailCodeError}</div>}
                    </div>
                  )}
                  {twofaStep === 'email_sent' && (
                    <div className="flex flex-col gap-3">
                      <label className="font-semibold">{t('twofa_enter_email_code')}</label>
                      <input
                        type="text"
                        value={emailCode}
                        onChange={e => setEmailCode(e.target.value)}
                        className="px-3 py-2 rounded border border-border bg-background text-text input-neon-focus"
                        maxLength={6}
                        autoFocus
                      />
                      <button
                        className="px-4 py-2 rounded-lg border border-border text-white font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0 button-glow"
                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                        onClick={emailCodeError === t('twofa_code_expired') ? async () => {
                          setEmailCodeError('');
                          setTwofaLoading(true);
                          await handleRequestEmailCode();
                          setTwofaLoading(false);
                        } : handleVerifyEmailCode}
                        disabled={twofaLoading || (!emailCode && emailCodeError !== t('twofa_code_expired'))}
                      >
                        {emailCodeError === t('twofa_code_expired') ? t('twofa_request_code_again') : t('twofa_verify_email_code')}
                      </button>
                      {emailCodeError && (
                        <div className="text-red-500">{emailCodeError}</div>
                      )}
                    </div>
                  )}
                  {twofaStep === 'email_verified' && qrData && (
                    <div className="flex flex-col gap-3 items-center">
                      <div className="mb-2">
                        <QRCode value={qrData.otpauth_url} />
                      </div>
                      <div className="text-xs md:text-sm bg-background rounded p-2 mb-2 break-all">
                        <b>{t('twofa_secret_key')}:</b> {qrData.secret}
                      </div>
                      <div className="text-left w-full mb-2">
                        <b>{t('twofa_instructions_title')}</b>
                        <ul className="list-disc pl-5">
                          <li>{t('twofa_instruction_scan')}</li>
                          <li>{t('twofa_instruction_manual')}</li>
                          <li>{t('twofa_instruction_save')}</li>
                          <li>{t('twofa_instruction_restore')}</li>
                          <li>{t('twofa_instruction_enter_code')}</li>
                        </ul>
                      </div>
                      <input
                        type="text"
                        value={totpCode}
                        onChange={e => setTotpCode(e.target.value)}
                        className="px-3 py-2 rounded border border-border bg-background text-text input-neon-focus"
                        maxLength={6}
                        placeholder={t('twofa_enter_totp_code')}
                        autoFocus
                      />
                      <button
                        className="px-4 py-2 rounded-lg border border-border text-white font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0 button-glow"
                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                        onClick={handleEnable2FA}
                        disabled={twofaLoading || !totpCode}
                      >
                        {t('twofa_enable')}
                      </button>
                      {totpError && <div className="text-red-500">{totpError}</div>}
                    </div>
                  )}
                  {twofaStep === 'final' && twofaSuccess && (
                    <div className="text-green-600 font-semibold text-center">
                      {t('twofa_success')}
                    </div>
                  )}
                </div>
              )}

              {/* Password Change Accordion */}
              <button
                className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openAccordion === 'password' ? 'shadow-lg button-glow' : ''}`}
                onClick={() => handleAccordionToggle('password')}
              >
                <span>{t('change_password')}</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${openAccordion === 'password' ? 'rotate-180' : ''}`} />
              </button>
              {openAccordion === 'password' && (
                <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in">
                  {pwStep === 'success' ? (
                    <div className="text-green-600 font-semibold text-center mb-2">{pwSuccess}</div>
                  ) : (
                    <form onSubmit={handleChangePassword} className="flex flex-col gap-3">
                      <label className="font-semibold">{t('current_password')}</label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={e => setCurrentPassword(e.target.value)}
                          className="px-3 py-2 rounded border border-border bg-background text-text w-full input-neon-focus"
                          autoComplete="current-password"
                        />
                        <button 
                          type="button" 
                          className="absolute right-2 top-2 focus:outline-none focus:ring-0 active:outline-none active:ring-0" 
                          style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                          onClick={() => setShowCurrentPassword(v => !v)}
                        >
                          {showCurrentPassword ? <Eye /> : <EyeClosed />}
                        </button>
                      </div>
                      <label className="font-semibold">{t('new_password')}</label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          className="px-3 py-2 rounded border border-border bg-background text-text w-full input-neon-focus"
                          autoComplete="new-password"
                        />
                        <button 
                          type="button" 
                          className="absolute right-2 top-2 focus:outline-none focus:ring-0 active:outline-none active:ring-0" 
                          style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                          onClick={() => setShowNewPassword(v => !v)}
                        >
                          {showNewPassword ? <Eye /> : <EyeClosed />}
                        </button>
                      </div>
                      <label className="font-semibold">{t('repeat_new_password')}</label>
                      <div className="relative">
                        <input
                          type={showRepeatNewPassword ? 'text' : 'password'}
                          value={repeatNewPassword}
                          onChange={e => setRepeatNewPassword(e.target.value)}
                          className="px-3 py-2 rounded border border-border bg-background text-text w-full input-neon-focus"
                          autoComplete="new-password"
                        />
                        <button 
                          type="button" 
                          className="absolute right-2 top-2 focus:outline-none focus:ring-0 active:outline-none active:ring-0" 
                          style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                          onClick={() => setShowRepeatNewPassword(v => !v)}
                        >
                          {showRepeatNewPassword ? <Eye /> : <EyeClosed />}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="px-4 py-2 rounded-lg border border-border text-white font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0 button-glow mt-2"
                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                        onClick={handleRequestPwEmailCode}
                        disabled={pwLoading}
                      >
                        {pwLoading ? t('sending') : t('twofa_request_email_code')}
                      </button>
                      <label className="font-semibold mt-2">{t('twofa_enter_email_code')}</label>
                      <input
                        type="text"
                        value={pwEmailCode}
                        onChange={e => setPwEmailCode(e.target.value)}
                        className="px-3 py-2 rounded border border-border bg-background text-text w-full input-neon-focus"
                        maxLength={6}
                      />
                      {is2FAEnabled && (
                        <>
                          <label className="font-semibold mt-2">2FA</label>
                          <input
                            type="text"
                            value={pw2faCode}
                            onChange={e => setPw2faCode(e.target.value)}
                            className="px-3 py-2 rounded border border-border bg-background text-text w-full input-neon-focus"
                            maxLength={6}
                          />
                        </>
                      )}
                      <button
                        type="submit"
                        className="px-4 py-2 rounded-lg border border-border text-white font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0 button-glow mt-2"
                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                        disabled={pwLoading}
                      >
                        {t('change_password')}
                      </button>
                      
                      {/* Remind Password Button */}
                      <button
                        type="button"
                        onClick={handleRemindPassword}
                        className="px-4 py-2 rounded-lg border border-border text-white font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0 button-glow mt-2"
                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                        disabled={remindPasswordLoading}
                      >
                        {remindPasswordLoading ? t('sending') : t('remind_password')}
                      </button>
                      
                      {remindPasswordMessage && (
                        <div className={`text-center text-sm mt-2 ${
                          remindPasswordMessage.includes('отправлен') ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {remindPasswordMessage}
                        </div>
                      )}
                      
                      {pwError && <div className="text-red-500 text-center mt-2">{pwError}</div>}
                    </form>
                  )}
                </div>
              )}

              {/* API Keys Accordion */}
              <button
                className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openAccordion === 'api_keys' ? 'shadow-lg button-glow' : ''}`}
                onClick={() => handleAccordionToggle('api_keys')}
              >
                <span className="flex items-center gap-2">
                  {t('api_keys_registration')}
                  {hasApiKeys && <Check className="w-5 h-5 text-green-500 ml-1" />}
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform ${openAccordion === 'api_keys' ? 'rotate-180' : ''}`} />
              </button>
              {openAccordion === 'api_keys' && (
                <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in">
                  {hasApiKeys ? (
                    <div className="flex flex-col gap-3">
                      {/* Кнопка показать/скрыть API ключи */}
                      <button
                        onClick={handleShowApiKeys}
                        className={`px-4 py-2 rounded-lg border border-border text-text font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${showApiKeys ? 'button-glow' : ''}`}
                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                      >
                        {showApiKeys ? t('hide_api_keys') : t('show_api_keys')}
                      </button>

                      {/* Простое отображение API ключей */}
                      {showApiKeys && apiKeysData && (
                        <div className="mt-3 p-4 bg-background border border-border rounded-lg">
                          <div className="space-y-3">
                            <div>
                              <div className="text-sm font-medium text-textSubtle mb-1">
                                {t('your_access_key')}:
                              </div>
                              <div className="px-3 py-2 bg-muted border border-border rounded text-text text-sm font-mono break-all">
                                {apiKeysData.access_key}
                              </div>
                            </div>
                            
                            <div>
                              <div className="text-sm font-medium text-textSubtle mb-1">
                                {t('your_secret_key')}:
                              </div>
                              <div className="px-3 py-2 bg-muted border border-border rounded text-text text-sm font-mono break-all">
                                {apiKeysData.secret_key}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={handleRevokeApiKeys}
                        className="px-4 py-2 rounded-lg border border-border text-text font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                      >
                        {t('revoke_api_keys')}
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSaveApiKeys} className="flex flex-col gap-3">
                      <label className="font-semibold">{t('enter_access_key')}</label>
                      <input
                        type="text"
                        value={accessKey}
                        onChange={e => setAccessKey(e.target.value)}
                        className="px-3 py-2 rounded border border-border bg-background text-text w-full input-neon-focus"
                        placeholder={t('access_key_placeholder')}
                      />
                      <label className="font-semibold">{t('enter_secret_key')}</label>
                      <input
                        type="text"
                        value={secretKey}
                        onChange={e => setSecretKey(e.target.value)}
                        className="px-3 py-2 rounded border border-border bg-background text-text w-full input-neon-focus"
                        placeholder={t('secret_key_placeholder')}
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 rounded-lg border border-border text-white font-semibold shadow-sm bg-transparent hover:bg-hover transition focus:outline-none focus:ring-0 active:outline-none active:ring-0 button-glow mt-2"
                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                        disabled={apiKeysLoading}
                      >
                        {t('confirm_api_keys')}
                      </button>
                      {apiKeysError && <div className="text-red-500 text-center mt-2">{apiKeysError}</div>}
                      {apiKeysSuccess && <div className="text-green-600 text-center mt-2">{apiKeysSuccess}</div>}
                    </form>
                  )}
                </div>
              )}
            </div>
          </section>
        ) : (
          <div className="flex flex-row flex-1 w-full items-stretch">
            {/* Sidebar */}
            <aside className="hidden md:flex flex-col w-72 min-w-[220px] max-w-xs bg-header border border-border rounded-lg p-4 mr-4 flex-shrink-0 flex-grow-0 min-h-[580px] h-full overflow-y-auto">
              <div>
                {botsLoading ? (
                  <div className="text-text text-center py-4">Загрузка ботов...</div>
                ) : botsError ? (
                  <div className="text-red-500 text-center py-4">{botsError}</div>
                ) : (
                  <>
                <div className="text-lg font-bold text-text mb-2">{t('available_bots')}</div>
                <ul className="mb-6">
                  {myBots.map(bot => (
                    <li key={bot.name}>
                      <button
                        className={`w-full flex items-center gap-3 h-12 px-4 rounded-lg mb-1 justify-start text-left border border-border transition-colors duration-200 focus:outline-none focus:ring-0 ${selectedBot === bot.name ? 'bg-primary/10 text-primary button-glow' : 'hover:bg-hover text-text'}`}
                        onClick={async (e) => { await handleBotSelect(bot.name); e.currentTarget?.blur(); }}
                      >
                        <img src={getCryptoIcons(bot.name).first} alt="First currency" className="w-[18px] h-[18px]" style={{ width: '18px', height: '18px', ...getCryptoIcons(bot.name).firstStyle }} />
                        <span>{bot.display_name}</span>
                        <img src={getCryptoIcons(bot.name).second} alt="Second currency" className="w-[18px] h-[18px]" style={{ width: '18px', height: '18px', ...getCryptoIcons(bot.name).secondStyle }} />
                        {unreadMessages[bot.name] > 0 && (
                          <div className="ml-auto bg-primary text-white text-xs font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1">
                            {unreadMessages[bot.name]}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="text-lg font-bold text-text mb-2">{t('locked_bots')}</div>
                <ul>
                  {availableBots.map(bot => (
                    <li key={bot.name}>
                      <button
                        className={`w-full flex items-center gap-3 h-12 px-4 rounded-lg mb-1 justify-start text-left border border-border transition-colors duration-200 focus:outline-none focus:ring-0 ${selectedBot === bot.name ? 'bg-primary/10 text-primary button-glow' : 'hover:bg-hover text-textSubtle'}`}
                        onClick={async (e) => { await handleBotSelect(bot.name); e.currentTarget?.blur(); }}
                      >
                        <img src={getCryptoIcons(bot.name).first} alt="First currency" className="w-[18px] h-[18px] opacity-50" style={{ width: '18px', height: '18px', ...getCryptoIcons(bot.name).firstStyle }} />
                        <span>{bot.display_name}</span>
                        <img src={getCryptoIcons(bot.name).second} alt="Second currency" className="w-[18px] h-[18px] opacity-50" style={{ width: '18px', height: '18px', ...getCryptoIcons(bot.name).secondStyle }} />
                        <LockIcon className="w-4 h-4 ml-auto text-textSubtle" />
                      </button>
                    </li>
                  ))}
                </ul>
                  </>
                )}
              </div>
            </aside>
            {/* Mobile sidebar */}
            {isMobile && mobileChatOpen ? null : (
              <aside className="flex md:hidden flex-col w-full mb-4">
                <div className="text-lg font-bold text-text mb-2">{t('available_bots')}</div>
                <div className="flex flex-col gap-2 w-full mb-4">
                  {myBots.map(bot => (
                    <button
                      key={bot.name}
                      className={"w-full flex items-center gap-3 h-12 px-4 rounded-lg border border-border justify-start text-left transition-colors duration-200 focus:outline-none focus:ring-0 button-glow hover:bg-hover text-text"}
                      onClick={async (e) => { await handleBotSelect(bot.name); e.currentTarget?.blur(); }}
                    >
                      <img src={getCryptoIcons(bot.name).first} alt="First currency" className="w-[18px] h-[18px]" style={{ width: '18px', height: '18px', ...getCryptoIcons(bot.name).firstStyle }} />
                      <span>{bot.display_name}</span>
                      <img src={getCryptoIcons(bot.name).second} alt="Second currency" className="w-[18px] h-[18px]" style={{ width: '18px', height: '18px', ...getCryptoIcons(bot.name).secondStyle }} />
                      {unreadMessages[bot.name] > 0 && (
                        <div className="ml-auto bg-primary text-white text-xs font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1">
                          {unreadMessages[bot.name]}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <div className="text-lg font-bold text-text mb-2">{t('locked_bots')}</div>
                <div className="flex flex-col gap-2 w-full">
                  {availableBots.map(bot => (
                    <button
                      key={bot.name}
                      className={"w-full flex items-center gap-3 h-12 px-4 rounded-lg border border-border justify-start text-left transition-colors duration-200 focus:outline-none focus:ring-0 hover:bg-hover text-textSubtle"}
                      onClick={async (e) => { await handleBotSelect(bot.name); e.currentTarget?.blur(); }}
                    >
                      <img src={getCryptoIcons(bot.name).first} alt="First currency" className="w-[18px] h-[18px] opacity-50" style={{ width: '18px', height: '18px', ...getCryptoIcons(bot.name).firstStyle }} />
                      <span>{bot.display_name}</span>
                      <img src={getCryptoIcons(bot.name).second} alt="Second currency" className="w-[18px] h-[18px] opacity-50" style={{ width: '18px', height: '18px', ...getCryptoIcons(bot.name).secondStyle }} />
                      <LockIcon className="w-4 h-4 ml-auto text-textSubtle" />
                    </button>
                  ))}
                </div>
              </aside>
            )}
            {/* Чат: десктоп — всегда справа, мобилка — только если открыт чат */}
            {(!isMobile || mobileChatOpen) && (
              isMobile ? (
                <div className="fixed inset-0 z-40 flex flex-col w-full h-[calc(100vh-5rem)] bg-background"
                     style={{ top: shouldUseMobileOffset ? `calc(5rem + ${safeAreaTop})` : '5rem' }}>
                  {/* Шапка чата */}
                  <div className="flex items-center border-b border-border bg-header px-2 py-2" style={{ minHeight: '44px' }}>
                    <button
                      className={`flex items-center gap-2 px-2 h-8 rounded-lg border border-border bg-transparent text-text font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-border/60 mr-2 active:bg-transparent ${isBackButtonClicked ? 'button-glow' : ''}`}
                      onClick={handleBackButtonClick}
                    >
                      <ChevronLeft className="w-4 h-4" /> {i18n.language === 'ENG' ? 'Back' : 'Назад'}
                  </button>
                    <div className="text-base font-bold text-text flex-1 text-center truncate flex items-center justify-center gap-2">
                      {selectedBot && (
                        <>
                          <img src={getCryptoIcons(selectedBot).first} alt="First currency" className="w-[18px] h-[18px]" style={{ width: '18px', height: '18px', ...getCryptoIcons(selectedBot).firstStyle }} />
                          <span>{allBots.find(b => b.name === selectedBot)?.display_name}</span>
                          <img src={getCryptoIcons(selectedBot).second} alt="Second currency" className="w-[18px] h-[18px]" style={{ width: '18px', height: '18px', ...getCryptoIcons(selectedBot).secondStyle }} />
                        </>
                      )}
                    </div>
                  </div>
                  {/* Черное тело чата */}
                  <div
                    key={selectedBot || 'no-bot'}
                    ref={chatContainerRef}
                    className="flex-1 bg-black overflow-y-auto p-4"
                    onClick={() => { if (isMobile && isCommandPanelOpen) setIsCommandPanelOpen(false); }}
                  >
                    {!selectedBot || !isBotAvailable(selectedBot) ? (
                      <div className="flex items-center justify-center h-full text-textSubtle">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-text mb-2">
                            {i18n.language === 'ENG' 
                              ? `To purchase a 30-day subscription and get access to the ${allBots.find(b => b.name === selectedBot)?.display_name || selectedBot} bot:`
                              : `Для оформления подписки на 30 дней и получения доступа к боту ${allBots.find(b => b.name === selectedBot)?.display_name || selectedBot}:`
                            }
                          </div>
                          <div className="text-base text-text space-y-3">
                            {i18n.language === 'ENG' ? (
                              <>
                                <div className="text-left">
                                  1. Transfer 80 USDT via TRC20 (Tron) network to the wallet{isMobile ? ':' : ' '}
                                  {isMobile ? (
                                    <>
                                      <br />
                                      <span className="font-mono text-primary break-all">
                                        {process.env.REACT_APP_WALLET_ADDRESS}
                                      </span>
                                      <br />
                                    </>
                                  ) : (
                                    <button
                                      onClick={copyWalletAddress}
                                      className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                                    >
                                      {process.env.REACT_APP_WALLET_ADDRESS}
                                      <Copy className="w-4 h-4" />
                                    </button>
                                  )}{' '}
                                  {isMobile ? '' : '(click to copy). '}Please make sure to account for the network fee when sending the payment.
                                </div>
                                <div className="text-left">
                                  2. After a successful transfer, send a screenshot with the transaction hash (TXID) to the support team:{' '}
                                  <TelegramIcon className="w-4 h-4 inline-block align-middle -mt-1 mx-1" />
                                  <a
                                    href={process.env.REACT_APP_SUPPORT_TELEGRAM_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline font-medium hover:underline focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                                  >
                                    {process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE}
                                  </a>
                                </div>
                                <div className="text-left">
                                  3. Once your subscription is confirmed and the bot is activated, go to the{' '}
                                  <span className="inline-flex items-center cursor-pointer font-bold text-primary hover:underline" onClick={() => navigate('/instructions')}>
                                    <Settings className="w-4 h-4 mr-1 -mb-0.8" />
                                    Instructions
                                  </span>{' '}
                                  section to learn about the bot control commands and its settings.
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-left">
                                  1) Переведите 80 USDT в сети TRC20 (tron) на кошелёк{isMobile ? ':' : ' '}
                                  {isMobile ? (
                                    <>
                                      <br />
                                      <span className="font-mono text-primary break-all">
                                        {process.env.REACT_APP_WALLET_ADDRESS}
                                      </span>
                                      <br />
                                    </>
                                  ) : (
                                    <button
                                      onClick={copyWalletAddress}
                                      className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                                    >
                                      {process.env.REACT_APP_WALLET_ADDRESS}
                                      <Copy className="w-4 h-4" />
                                    </button>
                                  )}{' '}
                                  {isMobile ? '' : '(кликните для копирования). '}При оплате учитывайте комиссию сети.
                                </div>
                                <div className="text-left">
                                  2) После успешного перевода отправьте скриншот с хэшем (TXID) транзакции в службу поддержки{' '}
                                  <TelegramIcon className="w-4 h-4 inline-block align-middle -mt-1 mx-1" />
                                  <a
                                    href={process.env.REACT_APP_SUPPORT_TELEGRAM_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline font-medium hover:underline focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                                  >
                                    {process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE}
                                  </a>
                                </div>
                                <div className="text-left">
                                  3) После приобретения подписки и активации бота перейдите в раздел{' '}
                                  <span className="inline-flex items-baseline cursor-pointer font-bold text-primary hover:underline" onClick={() => navigate('/instructions')}>
                                    <Settings className="w-4 h-4 mr-1 -mb-0.8" />
                                    Инструкции
                                  </span>{' '}
                                  , чтобы ознакомиться с командами управления ботом и его настройками.
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : chatMessages.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-textSubtle">
                        {t('chat_placeholder')}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {groupMessagesByDate(chatMessages).map((group) => (
                          <ChatMessageGroup
                            key={group.date}
                            date={group.date}
                            messages={group.messages}
                            botDisplayName={allBots.find(b => b.name === selectedBot)?.display_name || selectedBot || ''}
                            botName={selectedBot}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Нижний бар с кнопкой и аккордеоном */}
                  <div className="relative w-full flex-shrink-0" style={{ minHeight: 48 }}>
                    <button
                      className={`w-full bg-header border-t border-border p-2 flex items-center justify-center min-h-[48px] text-lg font-bold text-text transition focus:outline-none focus:ring-0${isCommandPanelOpen ? ' button-glow' : ''}`}
                      style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                      data-command-panel-btn
                      onClick={e => {
                        e.stopPropagation();
                        // Проверяем, доступен ли выбранный бот
                        const botAvailable = selectedBot ? isBotAvailable(selectedBot) : false;
                        if (!selectedBot || !botAvailable) {
                          return; // Не открываем аккордеон для недоступных ботов
                        }
                        setIsCommandPanelOpen((v) => !v);
                      }}
                    >
                      {i18n.language === 'ENG' ? 'Command panel' : 'Панель команд'}
                    </button>
                    {isCommandPanelOpen && isMobile && (
                      <div
                        ref={commandAccordionRef}
                        className="bg-black shadow-lg animate-fade-in flex flex-col items-center p-3 gap-1.5 w-full mt-1"
                        style={{ minWidth: 240, ...accordionStyle, zIndex: 50, position: 'fixed', bottom: 52 }}
                      >
                        {commandList.map((cmd, idx) => (
                          <button
                            key={idx}
                            className={`w-full flex items-center gap-3 px-3 bg-header py-1.5 rounded-lg border border-border text-text font-semibold shadow-sm transition focus:outline-none focus:ring-0 ${clickedCommandIndex === idx ? 'button-glow' : ''}`}
                            onClick={() => handleCommandClick(idx)}
                          >
                            <cmd.icon className="w-4 h-4 text-primary" />
                            <span className="text-sm">{i18n.language === 'ENG' ? cmd.en : cmd.ru}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {isCommandPanelOpen && !isMobile && (
                      <div
                        ref={commandAccordionRef}
                        className="bg-black border-x border-border shadow-lg animate-fade-in flex flex-col items-center p-4 gap-2 w-full"
                        style={{ minWidth: 240, position: 'absolute', left: 0, right: 0, bottom: 48, zIndex: 50 }}
                      >
                        {commandList.map((cmd, idx) => (
                          <button
                            key={idx}
                            className={`w-full flex items-center gap-3 px-4 bg-header py-1 text-sm rounded-lg border border-border text-text font-semibold shadow-sm transition focus:outline-none focus:ring-0 ${clickedCommandIndex === idx ? 'button-glow' : ''}`}
                            onClick={() => handleCommandClick(idx)}
                          >
                            <cmd.icon className="w-5 h-5 text-primary" />
                            <span>{i18n.language === 'ENG' ? cmd.en : cmd.ru}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <section ref={chatSectionRef} className="flex-1 flex flex-col min-h-[580px] h-full bg-header border border-border rounded-lg p-0 pb-[1px] relative overflow-hidden">
                  {/* Верхняя полоска */}
                  <div className="flex items-center border-b border-border px-4 py-2 min-h-[48px] flex-shrink-0">
                    <div className="text-xl font-bold text-text flex-1 text-center flex items-center justify-center gap-2">
                      {selectedBot && (
                        <>
                          <img src={getCryptoIcons(selectedBot).first} alt="First currency" className="w-[18px] h-[18px]" style={{ width: '18px', height: '18px', ...getCryptoIcons(selectedBot).firstStyle }} />
                          <span>{allBots.find(b => b.name === selectedBot)?.display_name}</span>
                          <img src={getCryptoIcons(selectedBot).second} alt="Second currency" className="w-[18px] h-[18px]" style={{ width: '18px', height: '18px', ...getCryptoIcons(selectedBot).secondStyle }} />
                        </>
                      )}
                    </div>
                  </div>
                  {/* Черное тело чата */}
                  <div 
                    key={selectedBot || 'no-bot'}
                    ref={chatContainerRef}
                    className="bg-black overflow-y-auto p-4 absolute inset-x-0"
                    style={{ 
                      top: '48px', 
                      bottom: '48px' 
                    }}
                  >
                    {!selectedBot || !isBotAvailable(selectedBot) ? (
                      <div className="flex items-center justify-center h-full text-textSubtle">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-text mb-2">
                            {i18n.language === 'ENG' 
                              ? `To purchase a 30-day subscription and get access to the ${allBots.find(b => b.name === selectedBot)?.display_name || selectedBot} bot:`
                              : `Для оформления подписки на 30 дней и получения доступа к боту ${allBots.find(b => b.name === selectedBot)?.display_name || selectedBot}:`
                            }
                          </div>
                          <div className="text-base text-text space-y-3">
                            {i18n.language === 'ENG' ? (
                              <>
                                <div className="text-left">
                                  1. Transfer 80 USDT via TRC20 (Tron) network to the wallet{' '}
                                  {isMobile ? (
                                    <span className="font-mono text-primary break-all">
                                      {process.env.REACT_APP_WALLET_ADDRESS}
                                    </span>
                                  ) : (
                                    <button
                                      onClick={copyWalletAddress}
                                      className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                                    >
                                      {process.env.REACT_APP_WALLET_ADDRESS}
                                      <Copy className="w-4 h-4" />
                                    </button>
                                  )}{' '}
                                  {isMobile ? '' : '(click to copy). '}Please make sure to account for the network fee when sending the payment.
                                </div>
                                <div className="text-left">
                                  2. After a successful transfer, send a screenshot with the transaction hash (TXID) to the support team:{' '}
                                  <TelegramIcon className="w-4 h-4 inline-block align-middle -mt-1 mx-1" />
                                  <a
                                    href={process.env.REACT_APP_SUPPORT_TELEGRAM_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline font-medium hover:underline focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                                  >
                                    {process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE}
                                  </a>
                                </div>
                                <div className="text-left">
                                  3. Once your subscription is confirmed and the bot is activated, go to the{' '}
                                  <span className="inline-flex items-baseline cursor-pointer font-bold text-primary hover:underline" onClick={() => navigate('/instructions')}>
                                    <Settings className="w-4 h-4 mr-1 -mb-0.8" />
                                    Instructions
                                  </span>{' '}
                                  section to learn about the bot control commands and its settings.
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-left">
                                  1) Переведите 80 USDT в сети TRC20 (tron) на кошелёк{' '}
                                  {isMobile ? (
                                    <span className="font-mono text-primary break-all">
                                      {process.env.REACT_APP_WALLET_ADDRESS}
                                    </span>
                                  ) : (
                                    <button
                                      onClick={copyWalletAddress}
                                      className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                                    >
                                      {process.env.REACT_APP_WALLET_ADDRESS}
                                      <Copy className="w-4 h-4" />
                                    </button>
                                  )}{' '}
                                  {isMobile ? '' : '(кликните для копирования). '}При оплате учитывайте комиссию сети.
                                </div>
                                <div className="text-left">
                                  2) После успешного перевода отправьте скриншот с хэшем (TXID) транзакции в службу поддержки{' '}
                                  <TelegramIcon className="w-4 h-4 inline-block align-middle -mt-1 mx-1" />
                                  <a
                                    href={process.env.REACT_APP_SUPPORT_TELEGRAM_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline font-medium hover:underline focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                                  >
                                    {process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE}
                                  </a>
                                </div>
                                <div className="text-left">
                                  3) После приобретения подписки и активации бота перейдите в раздел{' '}
                                  <span className="inline-flex items-baseline cursor-pointer font-bold text-primary hover:underline" onClick={() => navigate('/instructions')}>
                                    <Settings className="w-4 h-4 mr-1 -mb-0.8" />
                                    Инструкции
                                  </span>{' '}
                                  , чтобы ознакомиться с командами управления ботом и его настройками.
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : chatMessages.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-textSubtle">
                        {t('chat_placeholder')}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {groupMessagesByDate(chatMessages).map((group) => (
                          <ChatMessageGroup
                            key={group.date}
                            date={group.date}
                            messages={group.messages}
                            botDisplayName={allBots.find(b => b.name === selectedBot)?.display_name || selectedBot || ''}
                            botName={selectedBot}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Невидимый заполнитель для черной области */}
                  <div className="flex-1 min-h-0" style={{ maxHeight: 'calc(100% - 1px)' }}></div>
                  {/* Кнопка Панель команд (нижний бар) */}
                  <div className="relative w-full flex-shrink-0">
                    <button
                      ref={commandPanelBtnRef}
                      className={`w-full bg-header border-t border-border p-2 flex items-center justify-center min-h-[48px] rounded-b-lg text-lg font-bold text-text transition focus:outline-none focus:ring-0${isCommandPanelOpen && !isMobile ? ' button-glow' : ''}${(!selectedBot || !isBotAvailable(selectedBot)) ? ' opacity-50 cursor-not-allowed' : ''}`}
                      style={{ 
                        marginBottom: '1.5px',
                        width: isMobile ? 'calc(100% - 8px)' : 'calc(100% - 2px)',
                        marginLeft: isMobile ? '4px' : '1px',
                        ...(isMobile && isCommandPanelOpen ? { boxShadow: 'inset 0 0 10px 5px #00f' } : {}) 
                      }}
                      data-command-panel-btn
                      onClick={e => {
                        e.stopPropagation();
                        // Проверяем, доступен ли выбранный бот
                        const botAvailable = selectedBot ? isBotAvailable(selectedBot) : false;
                        if (!selectedBot || !botAvailable) {
                          return; // Не открываем аккордеон для недоступных ботов
                        }
                        setIsCommandPanelOpen((v) => !v);
                      }}
                    >
                      {i18n.language === 'ENG' ? 'Command panel' : 'Панель команд'}
                    </button>
                    {isCommandPanelOpen && isMobile && (
                      <div
                        ref={commandAccordionRef}
                        className="bg-header border-x border-t border-border shadow-lg animate-fade-in flex flex-col items-center p-3 gap-1.5 w-full"
                        style={{ minWidth: 240, ...accordionStyle, zIndex: 50, position: 'fixed' }}
                      >
                        {commandList.map((cmd, idx) => (
                          <button
                            key={idx}
                            className={`w-full flex items-center gap-3 px-3 bg-header py-1.5 text-sm rounded-lg border border-border text-text font-semibold shadow-sm transition focus:outline-none focus:ring-0 ${clickedCommandIndex === idx ? 'button-glow' : ''}`}
                            onClick={() => handleCommandClick(idx)}
                          >
                            <cmd.icon className="w-4 h-4 text-primary" />
                            <span>{i18n.language === 'ENG' ? cmd.en : cmd.ru}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {isCommandPanelOpen && !isMobile && (
                      <div
                        ref={commandAccordionRef}
                        className="bg-black border-x border-border shadow-lg animate-fade-in flex flex-col items-center p-4 gap-2 w-full"
                        style={{ minWidth: 240, position: 'absolute', left: 0, right: 0, bottom: 48, zIndex: 50 }}
                      >
                        {commandList.map((cmd, idx) => (
                          <button
                            key={idx}
                            className={`w-full flex items-center gap-3 px-4 bg-header py-2 rounded-lg border border-border text-text font-semibold shadow-sm transition focus:outline-none focus:ring-0 ${clickedCommandIndex === idx ? 'button-glow' : ''}`}
                            onClick={() => handleCommandClick(idx)}
                          >
                            <cmd.icon className="w-5 h-5 text-primary" />
                            <span>{i18n.language === 'ENG' ? cmd.en : cmd.ru}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
              </section>
              )
            )}
          </div>
        )}
      </main>

      {/* Модальное окно подтверждения отзыва API ключей */}
      {showRevokeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {t('confirm_api_revocation')}
            </h3>
            <p className="text-textSubtle mb-6">
              {t('confirm_api_revocation_message')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelRevokeApiKeys}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {t('no')}
              </button>
              <button
                onClick={confirmRevokeApiKeys}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {t('yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно статистики */}
      <StatsModal
        isOpen={isStatsModalOpen}
        onClose={() => setIsStatsModalOpen(false)}
        onPeriodSelect={handleStatsPeriodSelect}
        onDateSelect={handleStatsDateSelect}
      />

      {/* Модальное окно открытых ордеров */}
      {selectedBot && (
        <OrdersModal
          isOpen={isOrdersModalOpen}
          onClose={() => setIsOrdersModalOpen(false)}
          selectedBot={selectedBot}
          user={user}
        />
      )}

      {/* Модальное окно параметров */}
      {selectedBot && (
        <ParametersModal
          isOpen={isParametersModalOpen}
          onClose={() => setIsParametersModalOpen(false)}
          selectedBot={selectedBot}
          user={user}
          onParametersUpdated={handleParametersUpdated}
        />
      )}

      {/* Модальное окно разовых ордеров */}
      {selectedBot && (
        <OrderModal
          isOpen={isOrderModalOpen}
          onClose={() => setIsOrderModalOpen(false)}
          selectedBot={selectedBot}
          user={user}
          onOrderCreated={handleOrderCreated}
        />
      )}
    </div>
  );
};

// Иконка замка
function LockIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="8" width="12" height="8" rx="2" fill="currentColor"/>
      <path d="M7 8V6a3 3 0 1 1 6 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
} 